"use client"

import { useState, useEffect } from "react"
import { useRouter } from "next/navigation"
import { Button } from "@/components/ui/button"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import { Badge } from "@/components/ui/badge"
import { ChevronDown, Check, RefreshCw } from "lucide-react"
import { getUserSubjectConfigAction, setUserPrimarySubjectAction } from "@/lib/actions/user-subject-actions"
import { getSubjectsWithDetailsAction } from "@/lib/actions/subject-actions"
import type { Subject } from "@/types/subject"

interface PageSubjectSwitcherProps {
  currentSubject: Subject
  onSubjectChange?: (subject: Subject) => void
  className?: string
  variant?: "default" | "compact" | "title"
}

export function PageSubjectSwitcher({ 
  currentSubject, 
  onSubjectChange,
  className = "",
  variant = "default"
}: PageSubjectSwitcherProps) {
  const router = useRouter()
  const [subjects, setSubjects] = useState<Subject[]>([])
  const [loading, setLoading] = useState(false)
  const [open, setOpen] = useState(false)

  useEffect(() => {
    loadSubjects()
  }, [])

  const loadSubjects = async () => {
    try {
      const result = await getSubjectsWithDetailsAction()
      if (result.success && result.data) {
        setSubjects(result.data)
      }
    } catch (error) {
      console.error("加载科目失败:", error)
    }
  }

  const handleSubjectChange = async (subject: Subject) => {
    if (loading || subject.id === currentSubject?.id) return

    setLoading(true)
    setOpen(false)

    try {
      const result = await setUserPrimarySubjectAction(subject.id)
      
      if (result.success) {
        // 调用回调函数
        onSubjectChange?.(subject)
        
        // 根据当前页面路径决定跳转目标
        const currentPath = window.location.pathname

        if (currentPath.includes('/questions/chapter')) {
          router.push(`/questions`)
        } else if (currentPath.includes('/questions')) {
          router.push(`/questions`)
        } else if (currentPath.includes('/subjects')) {
          router.push(`/subjects`)
        } else if (currentPath.includes('/dashboard/')) {
          router.push(`/dashboard/${subject.id}`)
        }
        
        router.refresh()
      } else {
        alert(result.error || "切换科目失败")
      }
    } catch (error) {
      console.error("切换科目失败:", error)
      alert("切换失败，请稍后重试")
    } finally {
      setLoading(false)
    }
  }

  const getLevelText = (level: string) => {
    switch (level) {
      case "JUNIOR": return "初级"
      case "INTERMEDIATE": return "中级"
      case "SENIOR": return "高级"
      default: return "其他"
    }
  }

  const getLevelColor = (level: string) => {
    switch (level) {
      case "JUNIOR": return "bg-green-100 text-green-800"
      case "INTERMEDIATE": return "bg-blue-100 text-blue-800"
      case "SENIOR": return "bg-purple-100 text-purple-800"
      default: return "bg-gray-100 text-gray-800"
    }
  }

  // 标题样式变体
  if (variant === "title") {
    return (
      <div className={`flex items-center space-x-3 ${className}`}>
        <DropdownMenu open={open} onOpenChange={setOpen}>
          <DropdownMenuTrigger asChild>
            <Button 
              variant="ghost" 
              className="h-auto p-0 text-3xl font-bold text-gray-900 hover:text-blue-600 transition-colors"
              disabled={loading}
            >
              <span>{currentSubject.name}</span>
              {loading ? (
                <RefreshCw className="h-6 w-6 ml-2 animate-spin" />
              ) : (
                <ChevronDown className="h-6 w-6 ml-2" />
              )}
            </Button>
          </DropdownMenuTrigger>
          
          <DropdownMenuContent align="start" className="w-[320px]">
            <DropdownMenuLabel>切换考试科目</DropdownMenuLabel>
            <DropdownMenuSeparator />
            
            {subjects
              .filter(subject => subject.id !== currentSubject?.id)
              .map((subject) => (
                <DropdownMenuItem
                  key={subject.id}
                  onClick={() => handleSubjectChange(subject)}
                  className="flex items-center justify-between p-4 cursor-pointer"
                >
                  <div className="flex-1">
                    <div className="font-medium mb-1">{subject.name}</div>
                    <div className="flex items-center space-x-2">
                      <Badge className={getLevelColor(subject.level)} variant="secondary">
                        {getLevelText(subject.level)}
                      </Badge>
                      <span className="text-xs text-gray-500">{subject.code}</span>
                    </div>
                  </div>
                </DropdownMenuItem>
              ))}
          </DropdownMenuContent>
        </DropdownMenu>
        
        <Badge className={getLevelColor(currentSubject.level)}>
          {getLevelText(currentSubject.level)}资格
        </Badge>
      </div>
    )
  }

  // 紧凑样式变体
  if (variant === "compact") {
    return (
      <DropdownMenu open={open} onOpenChange={setOpen}>
        <DropdownMenuTrigger asChild>
          <Button 
            variant="outline" 
            size="sm"
            className={`${className} ${loading ? 'opacity-50' : ''}`}
            disabled={loading}
          >
            <span className="truncate">{currentSubject.name}</span>
            {loading ? (
              <RefreshCw className="h-3 w-3 ml-1 animate-spin" />
            ) : (
              <ChevronDown className="h-3 w-3 ml-1" />
            )}
          </Button>
        </DropdownMenuTrigger>
        
        <DropdownMenuContent align="start" className="w-[280px]">
          <DropdownMenuLabel>切换科目</DropdownMenuLabel>
          <DropdownMenuSeparator />
          
          {subjects
            .filter(subject => subject.id !== currentSubject?.id)
            .map((subject) => (
              <DropdownMenuItem
                key={subject.id}
                onClick={() => handleSubjectChange(subject)}
                className="flex items-center justify-between p-3 cursor-pointer"
              >
                <div className="flex-1">
                  <div className="font-medium mb-1">{subject.name}</div>
                  <div className="flex items-center space-x-2">
                    <Badge className={getLevelColor(subject.level)} variant="secondary">
                      {getLevelText(subject.level)}
                    </Badge>
                    <span className="text-xs text-gray-500">{subject.code}</span>
                  </div>
                </div>
              </DropdownMenuItem>
            ))}
        </DropdownMenuContent>
      </DropdownMenu>
    )
  }

  // 默认样式
  return (
    <div className={`flex items-center space-x-3 ${className}`}>
      <span className="text-sm text-gray-600">当前科目:</span>
      <DropdownMenu open={open} onOpenChange={setOpen}>
        <DropdownMenuTrigger asChild>
          <Button 
            variant="outline" 
            className={`min-w-[200px] justify-between ${loading ? 'opacity-50' : ''}`}
            disabled={loading}
          >
            <div className="flex items-center">
              <span className="truncate">{currentSubject.name}</span>
            </div>
            {loading ? (
              <RefreshCw className="h-4 w-4 ml-2 animate-spin" />
            ) : (
              <ChevronDown className="h-4 w-4 ml-2 opacity-50" />
            )}
          </Button>
        </DropdownMenuTrigger>
        
        <DropdownMenuContent align="start" className="w-[300px]">
          <DropdownMenuLabel>切换考试科目</DropdownMenuLabel>
          <DropdownMenuSeparator />
          
          {subjects
            .filter(subject => subject.id !== currentSubject?.id)
            .map((subject) => (
              <DropdownMenuItem
                key={subject.id}
                onClick={() => handleSubjectChange(subject)}
                className="flex items-center justify-between p-3 cursor-pointer"
              >
                <div className="flex-1">
                  <div className="font-medium mb-1">{subject.name}</div>
                  <div className="flex items-center space-x-2">
                    <Badge className={getLevelColor(subject.level)} variant="secondary">
                      {getLevelText(subject.level)}
                    </Badge>
                    <span className="text-xs text-gray-500">{subject.code}</span>
                  </div>
                </div>
              </DropdownMenuItem>
            ))}
        </DropdownMenuContent>
      </DropdownMenu>
      
      <Badge className={getLevelColor(currentSubject.level)}>
        {getLevelText(currentSubject.level)}资格
      </Badge>
    </div>
  )
}
